import { NavLink, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '../ErrorBoundary';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, CircleDashed, FileText, Check, Settings as SettingsIcon, LogOut, MessageSquare, Building2, ShieldCheck, ChevronLeft, ChevronRight, ChevronDown, Menu, X, GraduationCap, Home, Users, Plus, Loader2, HelpCircle, FolderTree, Star } from 'lucide-react';
import { WatchlistIcon } from '../icons/WatchlistIcon';
import { LocaleFlag } from '../../i18n/LocaleFlag';
import { useAuth } from '../../stores/authStore';
import { api } from '../../services/apiClient';
import { useProjectTree } from '../../stores/projectTreeStore';
import { useActiveOrgId, useActiveWorkspaceName } from '../../lib/useActiveOrg';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import { workspaceApi, WS_ROLE_LABELS, type Workspace } from '../../services/workspaceApi';
import { isWorkspaceAdminUser, isPureManager, isManagerUser } from '../../lib/roles';
import { selectBucketCount, useTrainingBucket } from '../../stores/trainingBucketStore';
import { useTheme, type Theme } from '../../stores/themeStore';
import {
  useLocale, isI18nSwitcherEnabled, SUPPORTED_UI_LOCALES, LOCALE_NATIVE_NAMES,
  PSEUDO_ENABLED, PSEUDO_LOCALE, applyLocale, type UiLocale,
} from '../../i18n';
import { ConfirmHost } from '../ui/ConfirmDialog';
import { TreeRailResizer } from '../tree';
import { OnboardingTour } from '../onboarding/OnboardingTour';
import { attachWatchBadgePollers } from '../../stores/watchStore';
import { InboxBell } from '../notifications/InboxBell';
import { ProjectTree } from '../projects/ProjectTree';
import { Wordmark } from '../Wordmark';
import { CommandPalette } from '../CommandPalette';
import { useIsMobile } from '../../hooks/useMediaQuery';

/** Primary workspace nav — shared by the desktop header strip and the mobile
 *  off-canvas drawer so both stay in sync from one source. Module-scope (not
 *  inside the component), so labels are `nav:` catalog KEYS, resolved via
 *  `t(n.labelKey)` at each render site — a hook can't run at module load. */
// Product-owner nav reorder (2026-07) — Projects sits immediately before
// Studio, and PageSnips sits immediately after Worksnips. Logbook was
// removed from this top-nav strip entirely; it now lives as a section
// inside Settings (see Settings.tsx's ws-settings-logbook tab) — the
// `/logbook` route + page are unchanged, just no longer top-nav-linked.
const NAV_ITEMS: { to: string; labelKey: string; end?: boolean; countSlot?: 'snipboard'; studio?: boolean; manager?: boolean; requiresManager?: boolean; processStudio?: boolean }[] = [
  { to: '/',          labelKey: 'nav:items.console',  end: true },
  { to: '/projects',  labelKey: 'nav:items.projects' },
  // Studio (TUFG-00 v1.1) — creator-seat only; opens the most recent guide in
  // the editor. Hidden for viewer-seat users (resolved via `canCreate` below).
  { to: '/studio',    labelKey: 'nav:items.studio',   studio: true },
  { to: '/worksnips', labelKey: 'nav:items.worksnips' },
  // BP 26-03 — PageSnip (formerly FlowSnip). Subscription-gated (plan G1 +
  // org G2); shown only when the /access probe reports canUse (resolved via
  // `canProcessStudio`). Placed right after Worksnips per product request.
  { to: '/pagesnip', labelKey: 'nav:items.processStudio', processStudio: true },
  { to: '/board',     labelKey: 'nav:items.snipBoard', countSlot: 'snipboard' },
  { to: '/shared',    labelKey: 'nav:items.sharedWithMe' },
  // Learning Center — consolidated trainee + manager surface. Shown to
  // everyone (My Learning is universal); the manager tabs (Overview / People /
  // Worksnips) self-gate on role inside the page.
  { to: '/learning',  labelKey: 'nav:items.learningCenter' },
  // BP 48/54 — top-level entry to the STANDALONE /allocation surface (Overview/
  // Board/Map sub-nav in AllocationLayout; no longer nested under /admin). Gated
  // on manager+ role via `requiresManager` (members would 403), mirroring the
  // backend requireManager.
  { to: '/allocation', labelKey: 'nav:items.allocation', requiresManager: true },
];

export function AppShell() {
  const { t } = useTranslation(['nav', 'common']);
  const user         = useAuth(s => s.user);
  // Top-nav home entry shows the CURRENT workspace name (truncated via CSS)
  // instead of a generic "Console" label, so it's always clear which
  // workspace you're in — including after switching into another org via
  // /w/:wsId. useActiveWorkspaceName resolves the active org against the
  // shared workspace cache (also used by WorkspaceMenuSection below, so
  // opening the avatar menu never fires a second fetch) and falls back to
  // the home org name, then "Console", so the label never flashes empty.
  const workspaceLabel = useActiveWorkspaceName(t('nav:items.console'));

  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('ws-sidebar-collapsed') === 'true';
  });

  // Mobile pass (2026-05-30) — below lg the desktop 3-panel layout can't fit,
  // so the primary nav + ProjectTree move into an off-canvas drawer behind a
  // hamburger. isMobile drives mount/unmount; drawerOpen drives slide-in.
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Mobile pass (2026-07) — the drawer is TWO full-height sections behind a
  // segmented switch ("Menu" = primary nav, "Projects" = the ProjectTree as
  // its own panel) instead of nav + tree stacked in one column. Stacked, the
  // ~11 nav rows left the tree a squeezed ~150px tail on a phone — unusable
  // for browsing. The chosen tab is sticky across open/close within the
  // session so a user browsing the tree reopens where they left off.
  const [drawerTab, setDrawerTab] = useState<'menu' | 'projects'>('menu');

  // Seat licensing (TUFG-00 v1.1) — only CREATOR-seat users see Studio / can
  // edit. `can_create` defaults to true when absent (no viewer seats issued
  // yet), so behaviour is unchanged until the backend stamps viewer seats.
  const navigate = useNavigate();
  const canCreate = (user as { can_create?: boolean } | null)?.can_create !== false;
  // TUFG seat scaling — collaboration affordances (AI assistant / thread
  // authoring) are for creator + collaborator seats; a basic 'viewer' (Tier 1)
  // only reads. Default true when seat_type is absent (legacy → creator).
  const seatType = (user as { seat_type?: string } | null)?.seat_type;
  const canCollaborate = seatType !== 'viewer';
  // BP 48 — the top-level "Allocation" entry is for managers/admins only
  // (mirrors backend requireManager); members would hit a 403.
  const isManager = isManagerUser(user);
  // BP 26-03 — Process Studio nav visibility. The /access probe is ungated and
  // returns { canUse } from the three-gate resolver; hidden until entitled.
  const [canProcessStudio, setCanProcessStudio] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { api } = await import('../../services/apiClient');
        const r = await api.get('/api/v1/process-studio/access');
        if (alive) setCanProcessStudio(!!r.data?.canUse);
      } catch { /* not entitled / offline → stay hidden (fail closed) */ }
    })();
    return () => { alive = false; };
  }, []);
  async function goToStudio() {
    setDrawerOpen(false);
    try {
      const { api } = await import('../../services/apiClient');
      const r = await api.get('/api/v1/guides', { params: { limit: 1, mine: 1 } });
      const list = (r.data?.guides ?? r.data ?? []) as Array<{ id?: string }>;
      const id = Array.isArray(list) ? list[0]?.id : undefined;
      navigate(id ? `/guides/${id}` : '/');
    } catch { navigate('/'); }
  }

  // Sprint-15 — start the unwatched-count poller. Refreshes immediately,
  // then on every window focus, and every 60s while the tab is active.
  // The teardown closure unwires the listener + interval on unmount so
  // dev hot-reload doesn't accumulate timers.
  useEffect(() => attachWatchBadgePollers(), []);

  // Wave-9 — route-aware ProjectTree. Wave-8 elevated the tree into the
  // global shell to fix an orphan-nav bug, but that bled "Projects" into
  // pages that have nothing to do with project navigation (Logbook ≡ time,
  // Inbox ≡ notifications, Settings ≡ preferences, Chat ≡ AI assistant).
  // The tree only earns its real estate where project navigation IS the
  // page's job — Console, Captures, Folders, Studio (jump-to-snip), and
  // /projects itself (we keep this one + delete the page-internal duplicate
  // in Change 2 of the Wave-9 plan).
  const loc = useLocation();
  // User feedback (2026-05-23): /inbox + /logbook felt orphan without the
  // global tree. The earlier "tree only where it's the page's primary nav"
  // rule traded consistency for breathing room and lost. Bringing both back
  // into the showTree list — the tree is the workspace's spatial anchor; an
  // empty viewport without it reads as a broken page.
  const loc_path = loc.pathname;
  // /worksnips is excluded on purpose: that page hosts the SAME <ProjectTree />
  // in its own aside (wired to its folder filter instead of route navigation),
  // so letting the shell render it too would show the tree twice.
  const showTree =
    loc_path === '/' ||
    loc_path === '/projects' ||
    loc_path === '/inbox' ||
    loc_path === '/logbook' ||
    loc_path.startsWith('/captures') ||
    loc_path.startsWith('/folders/') ||
    loc_path.startsWith('/guides/') ||
    loc_path.startsWith('/projects/');

  // BP 54 — while /projects is in Tree or Split view, a left-rail folder click
  // must NOT navigate to the folder detail (it would eject the user from the
  // view). Tree scopes its panel to the clicked folder; Split shows the whole
  // tree in both panes and simply ignores the scope. ProjectTree already writes
  // select(id) before consulting onFolderSelect, so no-op callbacks are all
  // that's needed to suppress the navigation fallback in both cases.
  const projectsView = useProjectTree(s => s.projectsView);
  const scopeTreeToProjectsPage = loc_path === '/projects' && (projectsView === 'tree' || projectsView === 'split');

  // Mobile drawer — offer the tree on /worksnips too. That page's own tree
  // aside is hidden below md (and excluded from `showTree` above to avoid a
  // desktop double-render), so without this a phone had NO folder navigation
  // on /worksnips at all. In the drawer a folder tap navigates to the folder
  // detail page, which is the right mobile behaviour.
  const showDrawerTree = showTree || loc_path === '/worksnips';
  // Routes without a tree fall back to the nav section (state is left alone so
  // returning to a tree route restores the user's last choice).
  const drawerSection: 'menu' | 'projects' = showDrawerTree ? drawerTab : 'menu';

  // Close the mobile drawer on navigation, on Escape, and whenever the
  // viewport grows back to desktop (where the drawer doesn't exist).
  useEffect(() => { setDrawerOpen(false); }, [loc_path]);
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    // Height comes from --ws-app-h (100dvh where supported), NOT h-screen/100vh:
    // on a phone 100vh is the large viewport, so an overflow-hidden shell sized
    // to it hides its own bottom edge behind the URL bar. See tokens.css.
    <div className="overflow-hidden bg-ws-bg text-ws-ink flex flex-col"
         style={{ height: 'var(--ws-app-h)' }}>
      <header className="ws-header">
        <div className="flex items-center gap-3 lg:gap-6 min-w-0">
          {/* Mobile (< lg): hamburger opens the off-canvas drawer holding the
              primary nav + ProjectTree. Hidden on desktop where the inline nav
              + 260px tree aside are always visible. */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-sm text-ws-ink-2 hover:text-ws-ink hover:bg-ws-surface flex-shrink-0"
            aria-label={t('nav:header.openMenu')}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>

          <NavLink to="/" aria-label={t('nav:header.goToDashboard')} className="inline-flex items-center flex-shrink-0">
            {/* v0.6.0 — extracted-path SVG wordmark (BP 10-ui-ux/05 §3). */}
            <Wordmark size={22} />
          </NavLink>

          <nav className="hidden lg:flex items-center gap-1" aria-label={t('nav:landmarks.primary')}>
            {/* Sprint-21 — primary nav consolidated to workspace entries only.
                Admin + Platform-Admin moved into the avatar UserMenu (top-right)
                so the header reads as a clean "where I'm working" strip rather
                than a graveyard of admin scopes. Ask also moved right so the
                conversational utilities cluster together. */}
            {NAV_ITEMS.map((n) => {
              // Manager-only entries (Training) are hidden from viewer seats —
              // same gate as Studio (managing content requires a creator seat).
              if (n.manager && !canCreate) return null;
              if (n.requiresManager && !isManager) return null;
              if (n.processStudio && !canProcessStudio) return null;
              if (n.studio) {
                if (!canCreate) return null;
                return (
                  <button key={n.to} type="button" onClick={goToStudio} className="ws-header__nav-item">
                    <span className="inline-flex items-center gap-1.5"><span>{t(n.labelKey)}</span></span>
                  </button>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-tour={n.to === '/' ? 'nav-console' : undefined}
                  title={n.to === '/' ? workspaceLabel : undefined}
                  className={({ isActive }) => `ws-header__nav-item${isActive ? ' is-active' : ''}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className={n.to === '/' ? 'max-w-[160px] truncate' : undefined}>{n.to === '/' ? workspaceLabel : t(n.labelKey)}</span>
                    {n.countSlot === 'snipboard' && <SnipBoardCountPill />}
                  </span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        <div
          // Sprint-12 onboarding tour — step 1 spotlights this whole
          // cluster (avatar + theme switcher + sign-out). It sits closest
          // to Chrome's puzzle-piece toolbar, so the spotlight reads as
          // "install the extension, look up-right".
          data-tour="header-user"
          className="flex items-center gap-2 text-sm">
          {/* Workspace switching lives in the avatar menu (single source of
              truth — see WorkspaceMenuSection in UserMenu). The old standalone
              header dropdown was removed to avoid two controls for one action. */}
          {/* Sprint-21 — Ask moved here from the left nav so all
              "utility" surfaces (chat, notifications, watch state, theme,
              account) sit on the right edge as a single conversational
              cluster. The left nav now reads purely as workspaces.
              TUFG — AI assistant is a creator/collaborator capability; hidden
              for basic viewer seats (the backend 402s them anyway). */}
          {canCollaborate && (
          <NavLink
            to="/chat"
            className={({ isActive }) => `ws-header__nav-item${isActive ? ' is-active' : ''}`}
            aria-label={t('nav:header.askAriaLabel')}
            title={t('nav:header.ask')}
          >
            <span className="inline-flex items-center gap-1.5">
              <MessageSquare size={14} strokeWidth={1.75} aria-hidden="true" />
              <span className="hidden sm:inline">{t('nav:header.ask')}</span>
            </span>
          </NavLink>
          )}
          {/* Training-bucket quick-access — only renders for managers when the
              bucket is non-empty, so they see it filling while browsing. */}
          {canCreate && <span className="hidden lg:contents"><TrainingBucketBadge /></span>}
          {/* Unwatched count moved off the header into the InboxBell dropdown
              (2026-06-01) to keep the top bar clean. The bell now surfaces an
              "N unwatched team captures" row linking to /?filter=unwatched. */}
          {/* Help & onboarding — role-scoped guides + the Worksnip lifecycle.
              Toggles: opening remembers where you were (sessionStorage) so the
              button — or the page's X — returns you there; clicking it again
              while on /help closes/unloads the page. */}
          <button
            type="button"
            onClick={() => {
              if (loc.pathname === '/help') {
                navigate(sessionStorage.getItem('worksnip.helpReturn') || '/');
              } else {
                sessionStorage.setItem('worksnip.helpReturn', loc.pathname + loc.search);
                navigate('/help');
              }
            }}
            title={loc.pathname === '/help' ? t('nav:header.closeHelp') : t('nav:header.help')}
            aria-label={t('nav:header.help')}
            aria-pressed={loc.pathname === '/help'}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-sm transition-colors ${loc.pathname === '/help' ? 'text-ws-accent-ink bg-ws-accent-quiet' : 'text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface-2'}`}
          >
            <HelpCircle size={18} strokeWidth={1.75} aria-hidden />
          </button>
          <InboxBell />
          {/* Sprint-21 — Avatar UserMenu now hosts Admin entry-points AND
              the theme picker (theme switcher folded in 2026-05-23 so the
              right cluster reads as Inbox · Unwatched · Avatar only).
              Org Admin: visible to org_admin and super_admin.
              Platform Admin: super-admin only (cross-tenant). */}
          <UserMenu />
        </div>
      </header>
      {/* Wave-9 — tree is route-aware (see `showTree` above). On pages where
          ProjectTree is the natural primary nav (Console, Captures, Folders,
          Studio, /projects) we mount the 260px aside; on Logbook / Inbox /
          Settings / Chat we skip it and the page gets the full viewport so
          its own primary surface (calendar, filter chips, section list) can
          breathe. Selection still lives in useProjectTree so cross-page
          jumps preserve context. */}
      <main className="flex flex-1 overflow-hidden relative min-h-0">
        {showTree && !isMobile && (
          <>
            <aside
              className="ws-tree-aside flex-shrink-0 border-r border-ws-border bg-ws-bg relative"
              style={{
                // Width tracks the CSS var so a drag updates layout live; the
                // var is published (and persisted) by useTreeRailWidth.
                width: isCollapsed ? 0 : 'var(--ws-tree-w)',
                transition: 'width 0.2s ease-in-out',
                overflow: 'hidden',
              }}
              aria-label={t('nav:landmarks.projectNavigation')}
            >
              <div style={{ width: 'var(--ws-tree-w)', height: '100%' }}>
                <ProjectTree
                  onFolderSelect={scopeTreeToProjectsPage ? () => {} : undefined}
                  onSpecialSelect={scopeTreeToProjectsPage ? () => {} : undefined}
                />
              </div>
              {!isCollapsed && <TreeRailResizer />}
            </aside>
            <button
              type="button"
              onClick={() => {
                const next = !isCollapsed;
                setIsCollapsed(next);
                localStorage.setItem('ws-sidebar-collapsed', String(next));
              }}
              className="absolute z-40 w-6 h-6 rounded-full border border-ws-border bg-ws-surface flex items-center justify-center text-ws-ink-3 hover:text-ws-ink shadow-sm transition-all duration-200"
              style={{
                left: isCollapsed ? 8 : 'calc(var(--ws-tree-w) - 12px)',
                top: 12,
              }}
              aria-label={isCollapsed ? t('nav:header.expandSidebar') : t('nav:header.collapseSidebar')}
              title={isCollapsed ? t('nav:header.expandSidebar') : t('nav:header.collapseSidebar')}
            >
              {isCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
            </button>
          </>
        )}
        {/* `flex flex-col min-h-0` is load-bearing, not decoration.
            This wrapper has a definite height (it's a stretched flex child of the
            row above), but it used to be `display: block` — so a page rooted at
            `flex-1` got NO height from it (flex-1 is inert outside a flex
            container). Those pages fell back to content height, grew past the
            viewport, and were silently CLIPPED by the `overflow-hidden` here:
            their own inner `overflow-y-auto` never had a bounded parent to scroll
            against. Rows and cards below the fold were simply unreachable
            (ProjectsPage, ProjectDetailPage, Logbook, Inbox). Pages rooted at
            `h-full` were unaffected, which is why the bug looked arbitrary.
            Making this a flex column resolves `flex-1` to a real height; `h-full`
            and explicit-height pages are unchanged. */}
        <div className="flex-1 overflow-hidden min-w-0 flex flex-col min-h-0">
          {/* Per-page error boundary (audit fe-rel-1) — an uncaught render error
              in one page shows a recover panel WITHOUT blanking the whole app, and
              resets when the user navigates (keyed on pathname). */}
          <ErrorBoundary resetKey={loc.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {/* Mobile off-canvas drawer (< lg) — TWO full-height sections behind a
          segmented switch: "Menu" (primary nav) and "Projects" (the tree as
          its own panel). Each section owns the whole drawer height with its
          own scroll region, so the tree is never a squeezed tail under the
          nav. Nav rows are ≥44px (WCAG 2.5.5). Mounted only on a narrow
          viewport while open. The scrim closes it; route change / Escape
          close it via effects above. */}
      {isMobile && drawerOpen && (
        <>
          <div className="ws-drawer-scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <aside className="ws-drawer-panel" aria-label={t('nav:drawer.navigationLabel')}>
            <div className="flex items-center justify-between px-4 flex-shrink-0 border-b border-ws-border" style={{ height: 45 }}>
              <Wordmark size={20} />
              <button
                type="button"
                className="inline-flex items-center justify-center w-10 h-10 rounded-sm text-ws-ink-2 hover:text-ws-ink hover:bg-ws-surface"
                aria-label={t('nav:header.closeMenu')}
                onClick={() => setDrawerOpen(false)}
              >
                <X size={20} strokeWidth={1.75} />
              </button>
            </div>

            {/* Section switch — only on routes that have a project tree. */}
            {showDrawerTree && (
              <div className="px-3 pt-3 pb-2 flex-shrink-0">
                <div className="ws-segmented ws-segmented--grid ws-segmented--cols-2" role="tablist" aria-label={t('nav:drawer.sectionsLabel')}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={drawerSection === 'menu'}
                    className={`ws-segmented__item min-h-[40px] ${drawerSection === 'menu' ? 'is-active' : ''}`}
                    onClick={() => setDrawerTab('menu')}
                  >
                    <Menu size={14} strokeWidth={1.75} aria-hidden="true" />
                    {t('nav:drawer.menu')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={drawerSection === 'projects'}
                    className={`ws-segmented__item min-h-[40px] ${drawerSection === 'projects' ? 'is-active' : ''}`}
                    onClick={() => setDrawerTab('projects')}
                  >
                    <FolderTree size={14} strokeWidth={1.75} aria-hidden="true" />
                    {t('nav:drawer.projects')}
                  </button>
                </div>
              </div>
            )}

            {drawerSection === 'projects' && showDrawerTree ? (
              /* Full-height tree panel. ProjectTree is h-full flex-col and
                 manages its own scroll region (filter pinned on top, "New
                 folder" pinned at the bottom) — the drawer must NOT wrap it
                 in another scroller or the pinned rows scroll away. */
              <div className="border-t border-ws-border flex-1 min-h-0">
                <ProjectTree
                  onFolderSelect={scopeTreeToProjectsPage ? () => setDrawerOpen(false) : undefined}
                  onSpecialSelect={scopeTreeToProjectsPage ? () => setDrawerOpen(false) : undefined}
                />
              </div>
            ) : (
              <nav className="p-2 flex flex-col gap-0.5 flex-1 min-h-0 overflow-y-auto" aria-label={t('nav:landmarks.primary')}>
                {NAV_ITEMS.map((n) => {
                  if (n.manager && !canCreate) return null;
                  if (n.requiresManager && !isManager) return null;
                  if (n.processStudio && !canProcessStudio) return null;
                  if (n.studio) {
                    if (!canCreate) return null;
                    return (
                      <button
                        key={n.to}
                        type="button"
                        onClick={goToStudio}
                        className="flex items-center min-h-[44px] px-3 py-2 rounded-sm text-sm font-medium text-ws-ink-2 hover:bg-ws-surface hover:text-ws-ink text-left"
                      >
                        <span className="inline-flex items-center gap-1.5"><span>{t(n.labelKey)}</span></span>
                      </button>
                    );
                  }
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      className={({ isActive }) =>
                        `flex items-center min-h-[44px] px-3 py-2 rounded-sm text-sm font-medium ${
                          isActive ? 'bg-ws-surface text-ws-ink' : 'text-ws-ink-2 hover:bg-ws-surface hover:text-ws-ink'
                        }`
                      }
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span>{t(n.labelKey)}</span>
                        {n.countSlot === 'snipboard' && <SnipBoardCountPill />}
                      </span>
                    </NavLink>
                  );
                })}
                {canCollaborate && (
                <NavLink
                  to="/chat"
                  className={({ isActive }) =>
                    `flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-sm text-sm font-medium ${
                      isActive ? 'bg-ws-surface text-ws-ink' : 'text-ws-ink-2 hover:bg-ws-surface hover:text-ws-ink'
                    }`
                  }
                >
                  <MessageSquare size={15} strokeWidth={1.75} aria-hidden="true" />
                  <span>{t('nav:header.ask')}</span>
                </NavLink>
                )}
              </nav>
            )}
          </aside>
        </>
      )}

      {/* App-wide confirmation modal + undo toast host. Subscribers register
          on mount so any component can call confirmDialog() / showUndoToast()
          imperatively without prop drilling. */}
      <ConfirmHost />
      {/* Sprint-12 — first-run 3-step onboarding tour. Renders nothing
          when user.onboarding_completed_at is set, so the cost for
          returning users is one Zustand selector + an early return. */}
      <OnboardingTour />
      {/* BP 36 — global ⌘K command palette (cross-snip search + nav). */}
      <CommandPalette />
    </div>
  );
}

/**
 * Training-bucket quick-access badge. Renders ONLY when the manager's bucket
 * holds at least one item — a subtle "Training bucket · N" chip that links to
 * the Learning Center's Bucket tab. The absence of the
 * chip is the signal the bucket is empty (no "· 0" noise). The count comes
 * straight from the persisted trainingBucketStore so it stays live while the
 * manager keeps right-clicking folders/worksnips into the bucket.
 */
function TrainingBucketBadge() {
  const { t } = useTranslation('nav');
  const count = useTrainingBucket(selectBucketCount);
  if (!count || count <= 0) return null;
  return (
    <Link
      to="/learning?tab=bucket"
      title={t('nav:trainingBucket.title', { count })}
      aria-label={t('nav:trainingBucket.ariaLabel', { count })}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs"
      style={{
        background: 'var(--ws-accent-quiet)',
        border: '1px solid var(--ws-border)',
        color: 'var(--ws-accent-ink)',
        transition: 'border-color var(--ws-duration-fast) var(--ws-ease)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ws-border-strong)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ws-border)'; }}
    >
      <GraduationCap size={13} strokeWidth={1.75} aria-hidden="true" />
      <span>{t('nav:trainingBucket.label')}</span>
      <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
      <span className="font-mono tabular-nums">{count}</span>
    </Link>
  );
}

/**
 * UserMenu — Sprint-18 dropdown next to the inbox bell. Replaces the
 * earlier bare avatar + "Sign out" button. As of Sprint-21 it also hosts
 * the theme picker (folded in from the separate ThemeSwitcher widget) and
 * the role-gated admin entry-points (Org Admin / Platform Admin). Order
 * top → bottom:
 *
 *   ┌────────────────────────────┐
 *   │ jane@example.com           │   (header — disabled, identity)
 *   ├────────────────────────────┤
 *   │ Theme                      │   (eyebrow)
 *   │ ☀  Light       ✓           │   ◀ Light / Dark / Silver radio
 *   │ 🌙 Dark                    │
 *   │ ◐  Silver                  │
 *   ├────────────────────────────┤
 *   │ ⚙  Settings                │   → /settings
 *   ├────────────────────────────┤  (only when admin)
 *   │ 🏢 Org Admin              │   → /admin
 *   │ 🛡  Platform Admin         │   → /platform-admin   (super only)
 *   ├────────────────────────────┤
 *   │ ⎋  Sign out                │   destructive — flushes the authStore
 *   └────────────────────────────┘
 *
 * Closes on outside-click and Escape. Uses the same `data-tour="header-user"`
 * anchor the OnboardingTour already references — that anchor lives on the
 * parent wrapper, so adding markup INSIDE doesn't change tour targeting.
 */
/**
 * WorkspaceMenuSection — the single workspace switcher, rendered INSIDE the
 * avatar menu where users look for account-level actions (the old standalone
 * header dropdown was removed; this is now the one switcher). Switch + create.
 * For super-admins the list is every client workspace (consulting model,
 * BP 03/45); members see the workspaces they belong to. Selecting one sets
 * activeOrgId + reloads so every surface re-scopes (apiClient sends X-Workspace-Id).
 */
function WorkspaceMenuSection({ onSwitch }: { onSwitch: () => void }) {
  const { t } = useTranslation(['nav', 'common']);
  const organization = useAuth(s => s.organization);
  const currentOrgId = useActiveOrgId();
  // Raw (nullable) store field — null means "home workspace", which is what
  // select()'s "already active?" check needs. useActiveOrgId() resolves the
  // home fallback in, so it can't tell "explicitly home" from "explicitly
  // this org" apart the way this comparison needs to.
  const rawActiveOrgId = useAuth(s => s.activeOrgId);
  const setActiveOrg = useAuth(s => s.setActiveOrg);
  // Workspace creation is platform-admin only (matches the backend gate). Org
  // admins / members switch between workspaces they belong to but don't spin up
  // new ones from here.
  const isSuperAdmin = useAuth(s => s.user?.is_super_admin === true);
  // Shared cache (frontend/src/stores/workspaceStore.ts) — the header label
  // above (useActiveWorkspaceName) already triggers the fetch on shell
  // mount; ensureLoaded() here is a no-op in the common case and only does
  // real work if this menu somehow renders first.
  const workspaces    = useWorkspaceStore(s => s.workspaces);
  const ensureLoaded  = useWorkspaceStore(s => s.ensureLoaded);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Default workspace — the workspace that auto-loads at login (see
  // authStore.ts's setAuth + routes.tsx's bootstrap redirect, both of which
  // read user.default_workspace_id). null = no preference (home org wins).
  const defaultWorkspaceId = useAuth(s => (s.user as { default_workspace_id?: string | null } | null)?.default_workspace_id ?? null);
  const [defaultBusy, setDefaultBusy] = useState<string | null>(null);
  const [defaultErr, setDefaultErr] = useState<string | null>(null);

  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

  const current = workspaces.find(w => w.id === currentOrgId);
  const multi = workspaces.length > 1;
  const f = filter.trim().toLowerCase();
  const shown = f ? workspaces.filter(w => w.name.toLowerCase().includes(f)) : workspaces;

  function select(w: Workspace) {
    const next = w.is_home ? null : w.id;
    if (next === (rawActiveOrgId || null)) { onSwitch(); return; } // already active
    // Workspace lives in the URL (`/w/<id>`). Hard-navigate so the router
    // rebuilds with the new basename + the bootstrap syncs activeOrgId.
    window.location.assign(`/w/${w.id}/`);
  }

  // Toggle this workspace as the default. Clicking the star on the CURRENT
  // default clears it (back to "no preference" — home org wins at login);
  // clicking it on any other workspace makes that one the new default
  // (server enforces active membership — see userController.updateSettings).
  async function toggleDefault(w: Workspace, e: React.MouseEvent) {
    e.stopPropagation();
    if (defaultBusy) return;
    const next = w.id === defaultWorkspaceId ? null : w.id;
    setDefaultBusy(w.id);
    setDefaultErr(null);
    try {
      await api.patch('/api/v1/me/settings', { default_workspace_id: next });
      // Mirror the change onto the in-memory user record so this menu (and
      // any other default_workspace_id reader) reflects it without a reload.
      useAuth.setState(s => (s.user ? { user: { ...s.user, default_workspace_id: next } } : {}));
    } catch {
      setDefaultErr(t('nav:workspace.defaultError'));
    } finally {
      setDefaultBusy(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setErr(null);
    try {
      const ws = await workspaceApi.create(name);
      setActiveOrg(ws.id);
      // A new workspace lands requires_plan_selection=true and must pick a paid
      // plan before it can be entered — EXCEPT for a platform admin, who the
      // router guard already exempts (routes.tsx: `!isSuper && ...`). Sending
      // them to /choose-plan anyway walled the platform owner out of a workspace
      // only they are allowed to create (creation is is_super_admin-only), and
      // asked them to pay for their own product. The org was created fine; this
      // hard nav just made it look like a billing failure.
      window.location.href = isSuperAdmin ? `/w/${ws.id}` : '/choose-plan';
    } catch (e: any) {
      setErr(e?.response?.data?.message || t('nav:workspace.createError'));
      setBusy(false);
    }
  }

  return (
    <>
      <p className="ws-eyebrow px-2 py-1.5">{multi ? t('nav:workspace.switchWorkspace') : t('nav:workspace.workspace')}</p>

      {multi ? (
        <>
          {/* Super-admin consultants can have many client workspaces — filter.
              Threshold is 5, not 8, because the list below starts scrolling at
              about 4 rows: between 5 and 8 workspaces there was no filter AND no
              visible scrollbar, so a newly created workspace — which sorts last,
              after the home org and then alphabetically — was simply invisible.
              It reads as "the workspace was never created". */}
          {workspaces.length > 5 && (
            <div className="px-2 pb-1.5">
              <input
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('nav:workspace.filterPlaceholder')}
                className="ws-input w-full !h-7 !text-xs"
                aria-label={t('nav:workspace.filterAriaLabel')}
              />
            </div>
          )}
          {/* Was `max-h-56` — 224px, about four ~50px rows. A fifth workspace was
              clipped with nothing on screen to suggest more existed. Scale with the
              viewport instead, and keep a hard ceiling so the menu can't outgrow a
              short window. */}
          <div className="max-h-[min(60vh,22rem)] overflow-y-auto">
            {shown.map(w => {
              const isCurrent = w.id === currentOrgId;
              const isDefault = w.id === defaultWorkspaceId;
              return (
                <div
                  key={w.id}
                  role="menuitemradio"
                  aria-checked={isCurrent}
                  className="w-full flex items-center gap-1 rounded-sm hover:bg-ws-surface-2"
                >
                  <button
                    type="button"
                    onClick={() => select(w)}
                    className="flex-1 min-w-0 flex items-center gap-2 px-2 py-2 text-sm text-left text-ws-ink-2 hover:text-ws-ink"
                  >
                    {w.is_home
                      ? <Home size={14} className="text-ws-ink-3 shrink-0" />
                      : <Building2 size={14} className="text-ws-ink-3 shrink-0" />}
                    <span className="flex-1 min-w-0 text-left">
                      <span className="block truncate">{w.name}</span>
                      <span className="block text-[10.5px] text-ws-ink-3">
                        {t(WS_ROLE_LABELS[w.role])}
                        {w.is_home ? ` · ${t('nav:workspace.yourWorkspaceSuffix')}` : ''}
                        {isDefault ? ` · ${t('nav:workspace.defaultSuffix')}` : ''}
                      </span>
                    </span>
                    {isCurrent && <Check size={14} className="text-ws-accent-ink shrink-0" />}
                  </button>
                  {/* Default-workspace pin — PATCHes /me/settings, so it auto-loads
                      at every future login (see authStore.ts's setAuth). */}
                  <button
                    type="button"
                    onClick={(e) => toggleDefault(w, e)}
                    disabled={defaultBusy !== null}
                    aria-pressed={isDefault}
                    title={isDefault ? t('nav:workspace.unsetDefault') : t('nav:workspace.setDefault')}
                    aria-label={isDefault ? t('nav:workspace.unsetDefault') : t('nav:workspace.setDefault')}
                    className="shrink-0 mr-1 p-1.5 rounded-sm text-ws-ink-3 hover:bg-ws-surface-3 hover:text-ws-ink disabled:opacity-50"
                  >
                    {defaultBusy === w.id
                      ? <Loader2 size={13} className="animate-spin" />
                      : <Star size={13} className={isDefault ? 'text-ws-accent-ink' : ''} fill={isDefault ? 'currentColor' : 'none'} />}
                  </button>
                </div>
              );
            })}
            {shown.length === 0 && <p className="px-2 py-2 text-xs text-ws-ink-3">{t('nav:workspace.noMatches')}</p>}
            {defaultErr && <p className="px-2 pt-1 text-[11px] text-ws-danger">{defaultErr}</p>}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 px-2 pb-1.5 text-sm text-ws-ink-2">
          <Home size={14} className="text-ws-ink-3 shrink-0" />
          <span className="truncate">{current?.name || organization?.name || t('nav:workspace.yourWorkspaceFallback')}</span>
        </div>
      )}

      {/* Create — platform-admin only (self-serve creation is gated; the backend
          returns 403 for non-super-admins). Org admins/members just switch. */}
      {isSuperAdmin && (!creating ? (
        <button
          type="button"
          onClick={() => { setCreating(true); setNewName(''); setErr(null); }}
          className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm text-ws-ink-3 hover:bg-ws-surface-2 hover:text-ws-ink"
        >
          <Plus size={14} /> {t('nav:workspace.newWorkspace')}
        </button>
      ) : (
        <form onSubmit={handleCreate} className="px-2 py-1.5 space-y-1.5">
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder={t('nav:workspace.namePlaceholder')}
            maxLength={100}
            className="ws-input w-full !h-7 !text-xs"
          />
          {err && <p className="text-[11px] text-ws-danger">{err}</p>}
          <div className="flex gap-1.5">
            <button type="submit" disabled={busy || !newName.trim()} className="ws-btn-primary ws-btn-sm flex-1 inline-flex items-center justify-center gap-1">
              {busy && <Loader2 size={11} className="animate-spin" />} {t('nav:workspace.create')}
            </button>
            <button type="button" onClick={() => setCreating(false)} className="ws-btn-tertiary ws-btn-sm">{t('common:actions.cancel')}</button>
          </div>
        </form>
      ))}

      <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
    </>
  );
}

function UserMenu() {
  const { t, i18n: i18nInstance } = useTranslation(['nav', 'common']);
  const user   = useAuth(s => s.user);
  const logout = useAuth(s => s.logout);
  const theme    = useTheme(s => s.theme);
  const setTheme = useTheme(s => s.setTheme);
  // BP 56 §5 — the avatar language switcher. Gated behind the platform
  // Setting('i18n_ui_enabled'), exposed on the auth payload as
  // user.platform_i18n_ui_enabled (+ a dev-only local override — see i18n/gate.ts).
  const i18nSwitcherEnabled = isI18nSwitcherEnabled(user);
  const setLocale = useLocale(s => s.setLocale);
  // BP 56 §5 — the language switcher is a collapsed dropdown (flag + native
  // name + chevron) that expands to the flag+name list, so the avatar menu
  // stays short. Matches the reference design (flag + name + checkmark).
  const [langOpen, setLangOpen] = useState(false);
  // The pseudolocale (qps) only ever appears when VITE_I18N_PSEUDO=1 (dev QA
  // sweep, BP 56 §10/§11) — never in a normal build. It's deliberately NOT a
  // real `UiLocale` (never persisted/PATCHed as a user preference), so its
  // "active" state is read straight off the live i18next instance
  // (`i18nInstance.language`, reactive via `useTranslation`) rather than the
  // persisted `useLocale` store — that one selector doubles as the "active"
  // check for BOTH real picks (which flow through the store) and a qps
  // preview (which doesn't).
  const switcherLocales: readonly string[] = PSEUDO_ENABLED ? [...SUPPORTED_UI_LOCALES, PSEUDO_LOCALE] : SUPPORTED_UI_LOCALES;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initial = (user?.display_name || user?.email || '?').charAt(0).toUpperCase();
  const identity = user?.display_name || user?.email || '';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="w-7 h-7 rounded-full bg-ws-ink text-ws-bg grid place-items-center text-xs font-semibold cursor-pointer"
        title={identity}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {initial}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-80 rounded-md border border-ws-border bg-ws-surface p-1
                     shadow-lg ws-anim-fade-up z-50"
          style={{ boxShadow: 'var(--ws-shadow-lg)' }}>
          <div className="px-2 py-1.5">
            {user?.display_name && (
              <div className="text-[13px] font-medium text-ws-ink truncate" title={user.display_name}>
                {user.display_name}
              </div>
            )}
            {/* Always surface the email so the user can confirm which account
                they're signed in as (previously hidden whenever a display name existed). */}
            {user?.email && (
              <div className="text-[11px] text-ws-ink-3 font-mono truncate" title={user.email}>
                {user.email}
              </div>
            )}
          </div>
          <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
          {/* Workspace switcher — lives at the top of the avatar menu so
              "switch workspace" is where users look for it. */}
          <WorkspaceMenuSection onSwitch={() => setOpen(false)} />
          {/* Sprint-21 — theme picker folded into the avatar menu so the
              top-right cluster stops sprouting independent dropdowns. The
              menu stays open across theme picks (no setOpen(false)) so the
              user can preview each option without re-opening. */}
          <p className="ws-eyebrow px-2 py-1.5">{t('nav:userMenu.theme')}</p>
          <div
            role="radiogroup"
            aria-label={t('nav:userMenu.theme')}
            className="flex gap-1 px-2 py-1.5"
            style={{ background: 'var(--ws-surface-2)', borderRadius: 8, padding: '4px' }}
          >
            {THEMES.map(({ id, labelKey }) => (
              <button
                key={id}
                role="radio"
                aria-checked={theme === id}
                aria-label={t(labelKey)}
                title={t(labelKey)}
                onClick={() => setTheme(id)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all"
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  background: theme === id ? 'var(--ws-tile-bg)' : 'transparent',
                  boxShadow: theme === id ? 'var(--ws-shadow-sm)' : 'none',
                  color: theme === id ? 'var(--ws-ink)' : 'var(--ws-ink-3)',
                  border: theme === id ? '1px solid var(--ws-border)' : '1px solid transparent',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.12)',
                    background: THEME_COLORS[id],
                    flexShrink: 0,
                  }}
                />
                <span>{t(labelKey)}</span>
              </button>
            ))}
          </div>
          <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
          {/* BP 56 §5 — language switcher: a collapsed dropdown (current flag +
              native name + chevron) that expands to a flag + name list with a
              checkmark on the active row (reference design). Collapsed keeps the
              avatar menu short; native names are never translated. */}
          {i18nSwitcherEnabled && (() => {
            const activeCode = i18nInstance.language;
            const activeName = activeCode === PSEUDO_LOCALE ? 'Pseudo'
              : (LOCALE_NATIVE_NAMES[activeCode as UiLocale] ?? activeCode);
            return (
              <>
                <p className="ws-eyebrow px-2 pt-1.5">{t('nav:userMenu.language')}</p>
                <div className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setLangOpen((o) => !o)}
                    aria-expanded={langOpen}
                    aria-label={t('nav:userMenu.language')}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm border border-ws-border text-ws-ink-2 hover:bg-ws-surface-2"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <LocaleFlag code={activeCode} size={18} />
                      <span className="truncate">{activeName}</span>
                    </span>
                    <ChevronDown size={14} className={`shrink-0 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {langOpen && (
                    <div role="radiogroup" aria-label={t('nav:userMenu.language')} className="mt-1 flex flex-col gap-0.5">
                      {switcherLocales.map((code) => {
                        const isActive = activeCode === code;
                        const nativeName = code === PSEUDO_LOCALE ? 'Pseudo' : LOCALE_NATIVE_NAMES[code as UiLocale];
                        return (
                          <button
                            key={code}
                            role="radio"
                            aria-checked={isActive}
                            onClick={() => {
                              // qps is a dev preview only — never persisted.
                              if (code === PSEUDO_LOCALE) { void applyLocale(PSEUDO_LOCALE); }
                              else { setLocale(code as UiLocale); }
                              setLangOpen(false);
                            }}
                            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm text-left transition-colors"
                            style={{
                              background: isActive ? 'var(--ws-accent-quiet)' : 'transparent',
                              color: isActive ? 'var(--ws-accent-ink)' : 'var(--ws-ink-2)',
                            }}
                          >
                            <span className="inline-flex items-center gap-2 min-w-0">
                              <LocaleFlag code={code} size={18} />
                              <span className="truncate">{nativeName}</span>
                            </span>
                            {isActive && <Check size={14} className="text-ws-accent-ink shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
              </>
            );
          })()}
          {/* Watchlist — the user's bookmarked Worksnips. Moved off the top nav
              into here (per product call); bookmark icon throughout. */}
          <button
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/watchlist'); }}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm
                       text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink">
            <WatchlistIcon size={15} />
            <span>{t('nav:userMenu.watchlist')}</span>
          </button>
          <button
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/settings'); }}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm
                       text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink">
            <SettingsIcon size={14} strokeWidth={1.75} />
            <span>{t('nav:userMenu.settings')}</span>
          </button>
          {/* Sprint-21 — admin entry-points moved off the top-nav into here.
              Order: Org Admin first (own scope) → Platform Admin second
              (cross-tenant, super-admin only). Section divider above so
              they read as a distinct cluster from the user prefs. */}
          {/* Admin / manager entry. Workspace admins get full Org Admin; a pure
              Work Manager gets the work-management slice (teams + assignments). */}
          {isWorkspaceAdminUser(user) ? (
            <>
              <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
              <button
                role="menuitem"
                onClick={() => { setOpen(false); navigate('/admin'); }}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm
                           text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink">
                <Building2 size={14} strokeWidth={1.75} />
                <span>{t('nav:userMenu.orgAdmin')}</span>
              </button>
            </>
          ) : isPureManager(user) ? (
            <>
              <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
              <button
                role="menuitem"
                onClick={() => { setOpen(false); navigate('/admin/cohorts'); }}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm
                           text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink">
                <Users size={14} strokeWidth={1.75} />
                <span>{t('nav:userMenu.teamAndAssignments')}</span>
              </button>
            </>
          ) : null}
          {user?.is_super_admin && (
            <button
              role="menuitem"
              onClick={() => { setOpen(false); navigate('/platform-admin'); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm
                         text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink">
              <ShieldCheck size={14} strokeWidth={1.75} />
              <span>{t('nav:userMenu.platformAdmin')}</span>
            </button>
          )}
          <div className="my-1 h-px bg-ws-border" aria-hidden="true" />
          <button
            role="menuitem"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm
                       text-ws-ink-2 hover:bg-ws-surface-2 hover:text-ws-ink">
            <LogOut size={14} strokeWidth={1.75} />
            <span>{t('nav:userMenu.signOut')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** Sprint-21 — themes lives at module-scope so UserMenu can render the
 *  radio inline. Sun / Moon / CircleDashed icons are imported once at the
 *  top of the file.
 *
 *  v0.6.0 — Paper added as the FIRST entry and the new default. Light, Dark,
 *  Silver remain selectable and visually unchanged. The leading PaperSwatch
 *  inline component renders the canvas + coral dot per BP §2. */
const THEMES: { id: Theme; labelKey: string; Icon: typeof Sun }[] = [
  { id: 'paper',  labelKey: 'nav:userMenu.themes.paper',  Icon: FileText },
  { id: 'light',  labelKey: 'nav:userMenu.themes.light',  Icon: Sun },
  { id: 'dark',   labelKey: 'nav:userMenu.themes.dark',   Icon: Moon },
  { id: 'silver', labelKey: 'nav:userMenu.themes.silver', Icon: CircleDashed },
];

/** Main background color per theme — used as the swatch in the toggle group. */
const THEME_COLORS: Record<Theme, string> = {
  paper:  '#E5E5E1',
  light:  '#fbfaf7',
  dark:   '#2b2a33',
  silver: '#ececec',
};

/**
 * Open-thread count pill rendered next to "Snip Board" in the primary nav.
 * Mirrors the prototype's nav strip where the badge surfaces "how much
 * conversation is live" without needing to land on the page. Only renders
 * when N > 0 to keep the nav clean. Polls every 60s while the tab is active.
 */
function SnipBoardCountPill() {
  const { t } = useTranslation('nav');
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    async function pull() {
      try {
        const { api } = await import('../../services/apiClient');
        const r = await api.get('/api/v1/threads', { params: { status: 'open', limit: 1 } });
        if (!active) return;
        // The endpoint returns the list — for an accurate total we'd need
        // a separate count endpoint. For now we render the number of items
        // in the first page (limit 100 is the API ceiling) as a usable
        // approximation. Add a `?count_only=1` shortcut if usage shows we
        // need an exact total above 100.
        const list = (r.data?.threads || []) as unknown[];
        setCount(list.length);
      } catch {
        if (active) setCount(null);
      }
    }
    void pull();
    const onFocus = () => void pull();
    const interval = window.setInterval(pull, 60_000);
    window.addEventListener('focus', onFocus);
    return () => {
      active = false;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  }, []);

  if (count == null || count === 0) return null;
  return (
    <span
      aria-label={t('nav:snipBoardPill.ariaLabel', { count })}
      style={{
        fontSize: 10.5,
        padding: '1px 6px',
        borderRadius: 999,
        background: 'var(--ws-accent-quiet)',
        color: 'var(--ws-accent-ink)',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
      }}
    >
      {count}
    </span>
  );
}
